/**
 * @description Month grid for company holidays in Global Settings (LYN-103)
 *
 * Key features:
 * - Full-size month grid (settings MonthView density — holiday pills only)
 * - Responsive weekday headers (Sun–Sat desktop, S M T W T F S mobile)
 * - Prev / next / today navigation
 *
 * @dependencies
 * - lucide-react icons
 *
 * @notes
 * - Not scheduling CalendarView / EventPill / assignees
 */

import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const WEEKDAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * @param {Date} cursor
 * @returns {{ year: number, month: number, label: string }}
 */
function monthMeta(cursor) {
  return {
    year: cursor.getFullYear(),
    month: cursor.getMonth(),
    label: cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
  };
}

/**
 * Build 42 cells for a month grid.
 * @param {number} year
 * @param {number} month 0-based
 * @param {Array} holidays company holidays with observedDate
 */
function buildCells(year, month, holidays) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const byDate = new Map();
  (holidays || []).forEach((h) => {
    const key = String(h.observedDate || h.observed_date || '').slice(0, 10);
    if (!key) return;
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(h);
  });

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const dayOffset = i - startDow;
    let day;
    let inMonth;
    let cellYear = year;
    let cellMonth = month;

    if (dayOffset < 0) {
      inMonth = false;
      day = prevMonthDays + dayOffset + 1;
      cellMonth = month - 1;
      if (cellMonth < 0) {
        cellMonth = 11;
        cellYear = year - 1;
      }
    } else if (dayOffset >= daysInMonth) {
      inMonth = false;
      day = dayOffset - daysInMonth + 1;
      cellMonth = month + 1;
      if (cellMonth > 11) {
        cellMonth = 0;
        cellYear = year + 1;
      }
    } else {
      inMonth = true;
      day = dayOffset + 1;
    }

    const mm = String(cellMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const dateKey = `${cellYear}-${mm}-${dd}`;
    cells.push({
      key: `${dateKey}-${i}`,
      day,
      inMonth,
      dateKey,
      holidays: byDate.get(dateKey) || [],
    });
  }
  return cells;
}

/**
 * @param {object} props
 * @param {Date} props.cursorMonth
 * @param {(d: Date) => void} props.onCursorMonthChange
 * @param {Array} props.holidays
 * @param {string|null} [props.selectedDate]
 * @param {(dateKey: string, holiday: object|null) => void} [props.onSelectDay]
 */
const HolidayMonthGrid = ({
  cursorMonth,
  onCursorMonthChange,
  holidays,
  selectedDate = null,
  onSelectDay,
}) => {
  const { year, month, label } = monthMeta(cursorMonth);
  const cells = useMemo(
    () => buildCells(year, month, holidays),
    [year, month, holidays]
  );

  const goToday = () => {
    const now = new Date();
    onCursorMonthChange(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const shiftMonth = (delta) => {
    onCursorMonthChange(new Date(year, month + delta, 1));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-bold text-gray-900">Company holiday calendar</h3>
          <p className="text-xs text-gray-500 mt-0.5 font-medium">
            Full-size month grid — holiday pills only, not shifts.
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={goToday}
            className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Today
          </button>
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => shiftMonth(-1)}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="min-w-[7.5rem] text-center text-sm font-bold text-gray-900">
            {label}
          </span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => shiftMonth(1)}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-t border-gray-100">
        {WEEKDAYS_FULL.map((d, i) => (
          <div
            key={`wd-${d}-${i}`}
            className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-400 py-2 border-b border-gray-100"
          >
            <span className="sm:hidden">{WEEKDAYS_SHORT[i]}</span>
            <span className="hidden sm:inline">{d}</span>
          </div>
        ))}

        {cells.map((cell) => {
          const primary = cell.holidays[0] || null;
          const selected = selectedDate === cell.dateKey && Boolean(primary);
          const premium = Number(primary?.premiumMultiplier ?? primary?.premium_multiplier ?? 1);
          const paid = primary
            ? Boolean(primary.employeePaid ?? primary.employee_paid)
            : false;

          return (
            <button
              key={cell.key}
              type="button"
              disabled={!primary}
              onClick={() => onSelectDay?.(cell.dateKey, primary)}
              className={cn(
                'min-h-[52px] sm:min-h-[72px] p-1 sm:p-1.5 text-left border-b border-r border-gray-100 transition-colors',
                cell.inMonth ? 'bg-white' : 'bg-gray-50/60',
                primary ? 'cursor-pointer hover:bg-brand-light/40' : 'cursor-default',
                selected && 'bg-brand-light/60 ring-1 ring-inset ring-brand/30'
              )}
            >
              <span
                className={cn(
                  'text-xs font-semibold',
                  cell.inMonth ? 'text-gray-800' : 'text-gray-300',
                  primary && 'text-gray-900'
                )}
              >
                {cell.day}
              </span>
              {primary && (
                <div className="mt-0.5 space-y-0.5 min-w-0">
                  <span className="block text-[10px] sm:text-xs font-semibold text-brand leading-tight truncate sm:whitespace-normal sm:line-clamp-2">
                    {primary.name}
                  </span>
                  <div className="hidden sm:flex flex-wrap gap-1">
                    <span className="inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-gray-100 text-gray-600">
                      {paid ? 'paid' : 'unpaid'}
                    </span>
                    {premium > 1 && (
                      <span className="inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700">
                        {premium}× work
                      </span>
                    )}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HolidayMonthGrid;
